import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Header } from "@/components/site/Header";
import { ReadingProgressBar } from "@/components/site/ReadingProgressBar";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ---------------- Table of contents ---------------- */

const TOC = [
  { id: "real-time-payments", title: "Real-Time Payments Are Changing Financial Operations" },
  { id: "cross-border-rebuilt", title: "Cross-Border Payments Are Being Rebuilt From the Inside" },
  { id: "invoicing-compliance", title: "Invoicing Is Turning Into a Live Compliance Process" },
  { id: "new-settlement-rails", title: "New Settlement Rails Are Emerging Alongside the Traditional System" },
  { id: "operational-impact", title: "The Operational Impact on Financial Operations" },
];

/* ---------------- Shared typographic atoms ---------------- */

const P = ({ children, className = "" }) => (
  <p className={`mt-5 text-base leading-[1.75] text-muted-foreground ${className}`}>{children}</p>
);

const Note = ({ children, testId }) => (
  <div
    className="mt-6 rounded-xl border-l-[3px] border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/[0.05] px-6 py-5"
    data-testid={testId}
  >
    <p className="text-[0.95rem] leading-relaxed text-foreground/80">{children}</p>
  </div>
);

const Section = ({ id, index, title, children }) => (
  <section id={id} className="scroll-mt-28 pt-14 first:pt-0" data-testid={`article-section-${id}`}>
    <div className="flex items-baseline gap-4">
      <span className="text-sm font-medium tracking-[0.08em] text-[hsl(174_86%_24%)]">
        {String(index).padStart(2, "0")}
      </span>
      <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[1.7rem]">
        {title}
      </h2>
    </div>
    <div className="mt-1 border-t border-[hsl(var(--primary))]/10 pt-1">{children}</div>
  </section>
);

/* ---------------- Hero ---------------- */

const ArticleHero = () => (
  <section data-testid="article-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb" data-testid="article-breadcrumb">
        <Link
          to="/resources"
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid="article-breadcrumb-resources-link"
        >
          Resources
        </Link>
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
        <span className="text-foreground/80">How Business Payments Are Changing</span>
      </nav>

      {/* Hero panel */}
      <div
        className="mt-10 rounded-3xl bg-[hsl(var(--primary))]/[0.05] px-[14px] py-[33px] sm:px-[38px] sm:py-[49px] lg:px-[54px] lg:py-[65px]"
        data-testid="article-hero-panel"
      >
        <h1 className="font-serif-editorial max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          How Business Payments Are Changing
        </h1>
        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="flex items-center gap-2.5">
            <span className="h-[3px] w-5 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(174_86%_24%)]">
              Article
            </span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            4 min read
          </span>
        </div>
        <div className="mt-8 max-w-4xl lg:max-w-[1080px]">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            For years, conversations about the future of payments have focused
            on which new technology might replace the banking system. What is
            actually happening across payment infrastructure is quieter than
            that: money is moving faster, cross-border messaging is being
            rebuilt from the inside, and invoicing is turning into a live
            regulatory process rather than a document a company designs{" "}
            <br className="hidden lg:block" />
            on its own terms.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="article-hero-tags">
          {["Payments"].map((t) => (
            <span
              key={t}
              className="rounded-full border border-[hsl(var(--primary))]/25 px-4 py-1.5 text-sm font-medium text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Sticky table of contents with scrollspy ---------------- */

const TocSidebar = () => {
  const [activeId, setActiveId] = useState(TOC[0].id);

  useEffect(() => {
    const onScroll = () => {
      let current = TOC[0].id;
      for (const item of TOC) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) current = item.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <aside className="hidden lg:block">
      <nav
        className="sticky top-24 rounded-2xl bg-card/80 p-7 px-[23px] shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px]"
        aria-label="Table of contents"
        data-testid="article-toc"
      >
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Table of Contents
        </h2>
        <ol className="mt-5 space-y-1">
          {TOC.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                className={`block rounded-md py-1.5 pr-2 text-sm leading-snug transition-colors hover:text-[hsl(174_86%_24%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  activeId === s.id
                    ? "font-medium text-[hsl(174_86%_24%)]"
                    : "text-muted-foreground"
                }`}
                data-testid={`article-toc-link-${s.id}`}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </aside>
  );
};

/* ---------------- Article body ---------------- */

const ArticleBody = () => (
  <section data-testid="article-body-section">
    <Container>
      <div className="grid gap-12 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-16">
        <TocSidebar />

        <article className="max-w-[76ch]" data-testid="article-content">
          {/* 01 — Real-Time Payments */}
          <Section id="real-time-payments" index={1} title="Real-Time Payments Are Changing Financial Operations">
            <P>
              A decade ago, instant payments were a novelty limited to a
              handful of countries. That is no longer the case. Global
              real-time payment transactions reached 266.2 billion in 2023,
              growing more than 40% in a single year, according to research
              from ACI Worldwide.
            </P>
            <P>
              What matters here isn't the size of the number so much as what it
              represents: a shift in how quickly money actually moves once it
              leaves a company's account. Cash positions that used to update
              overnight now shift within minutes, and reconciliation that once
              had a day or two of breathing room needs to happen close to the
              moment a payment clears.
            </P>
            <Note testId="article-note-continuous-activity">
              This creates a broader operational shift: finance teams are
              moving away from managing payments as individual events and
              toward managing them as continuous financial activity.
            </Note>
          </Section>

          {/* 02 — Cross-Border Rebuilt */}
          <Section id="cross-border-rebuilt" index={2} title={<>Cross-Border Payments Are Being Rebuilt From{" "}<br className="hidden lg:block" />the Inside</>}>
            <P>
              The evolution of cross-border payments is about more than speed.
              The information attached to a transaction is becoming just as
              important as the movement of funds itself.
            </P>
            <P>
              For decades, cross-border payments ran on a messaging format
              built in the 1970s, designed around brief, unstructured text
              fields. SWIFT has spent the past few years replacing it, and the
              transition is nearly complete. When the old and new formats
              stopped running side by side in late 2025, the vast majority of
              payment instructions on the network had already switched over.
            </P>
            <P>
              A second change follows in November 2026, when free-text
              addresses disappear from cross-border payment instructions
              entirely, replaced by structured country and city fields.
            </P>
            <P>
              The practical effect is a payment record that finally carries
              enough detail to work with: cleaner counterparty information,
              better remittance data, and a reference number that makes each
              payment traceable throughout the settlement process.
            </P>
            <h3 className="mt-10 text-xl font-semibold tracking-[-0.01em] text-foreground">
              How Payment Data Is Evolving
            </h3>
            <div className="mt-5 overflow-hidden rounded-xl ring-1 ring-[hsl(var(--primary))]/12" data-testid="article-payment-data-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-[hsl(var(--primary))]/12 bg-[hsl(var(--primary))]/[0.05] hover:bg-[hsl(var(--primary))]/[0.05]">
                    <TableHead className="h-12 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      Earlier Payment Workflows
                    </TableHead>
                    <TableHead className="h-12 border-l border-[hsl(var(--primary))]/10 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      Modern Payment Infrastructure
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    ["Limited transaction information", "Structured and richer payment data"],
                    ["Manual reconciliation processes", "More opportunities for automation"],
                    ["Delayed visibility after settlement", "Faster access to payment status"],
                    ["Separate payment and reporting workflows", "More connected financial operations"],
                  ].map(([before, after]) => (
                    <TableRow key={before} className="border-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/[0.02]">
                      <TableCell className="whitespace-normal px-5 py-4 align-top text-sm leading-relaxed text-muted-foreground">
                        {before}
                      </TableCell>
                      <TableCell className="whitespace-normal border-l border-[hsl(var(--primary))]/10 px-5 py-4 align-top text-sm leading-relaxed text-muted-foreground">
                        {after}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Note testId="article-note-narrowing-window">
              Companies whose systems still expect the old, loosely structured
              fields have a narrowing window to catch up.
            </Note>
          </Section>

          {/* 03 — Invoicing */}
          <Section id="invoicing-compliance" index={3} title="Invoicing Is Turning Into a Live Compliance Process">
            <P>
              Invoicing is undergoing a similar shift. More than 90 countries
              now require structured, government-monitored e-invoicing or have
              firm plans to introduce it, turning what used to be an internal
              business document into something closer to a real-time filing.
            </P>
            <P>
              Belgium is a good illustration of how quickly this can take hold.
              When structured B2B e-invoicing became mandatory there at the
              start of 2026, its national invoicing network passed a million
              registered participants within weeks.
            </P>
            <P>
              Invoices increasingly need to be issued in a government-defined
              format and, in a growing number of countries, reported to tax
              authorities close to the moment they are created.
            </P>
          </Section>

          {/* 04 — New Settlement Rails */}
          <Section id="new-settlement-rails" index={4} title="New Settlement Rails Are Emerging Alongside the Traditional System">
            <P>
              The modernization of payment infrastructure is happening in more
              than one direction. Traditional payment networks are becoming
              faster and more structured, while new settlement models are
              emerging alongside them.
            </P>
            <P>
              Stablecoin-based settlement is one example of this shift. These
              models provide another option for cross-border transactions and
              high-volume payouts, particularly in situations where speed,
              availability, and global reach are important.
            </P>
            <Note testId="article-note-same-considerations">
              For businesses, the operational considerations remain the same
              across different payment methods: maintaining accurate
              transaction records, meeting compliance requirements, reconciling
              payments, and supporting financial reporting.
            </Note>
          </Section>

          {/* 05 — Operational Impact */}
          <Section id="operational-impact" index={5} title="The Operational Impact on Financial Operations">
            <P>
              These shifts describe payments becoming faster, more structured,
              and more closely connected to real-time reporting. This has
              direct implications for financial operations.
            </P>
            <P>
              Reconciliation processes built around next-day settlement will
              need to adapt to payment flows that settle within seconds.
              Payment records stored in loose, free-text formats will require
              more structure as SWIFT standards and e-invoicing requirements
              introduce new expectations around transaction data.
            </P>
            <P>
              Evaluating a new payment method also requires looking beyond
              speed and cost. Reporting, compliance, accounting integration,
              and internal controls are becoming important factors in how
              businesses assess payment infrastructure.
            </P>
            <Note testId="article-note-direction-clear">
              The pace differs by country and industry, but the direction is
              clear. Understanding these changes early gives businesses more
              time to adjust their processes and controls as payment
              infrastructure continues to evolve.
            </Note>
          </Section>
        </article>
      </div>
    </Container>
  </section>
);

/* ---------------- Final CTA ---------------- */

const ArticleFinalCta = () => (
  <section data-testid="article-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Getting Your Team Ready for Stablecoins
          </h2>
          <div className="mx-auto max-w-2xl lg:max-w-none">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              This article covered how real-time payments, standardized
              messaging, and e-invoicing mandates are reshaping{" "}
              <br className="hidden lg:block" />
              payment infrastructure. WFII programs build on these changes,
              helping finance professionals understand how{" "}
              <br className="hidden lg:block" />
              modern payment infrastructure affects accounting workflows,
              controls, and reporting processes.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="article-final-cta-explore-programs-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Page ---------------- */

export default function ArticlePaymentsChangingPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <ReadingProgressBar />
        <main className="wfii-longform flex-1 space-y-[60px] lg:space-y-[100px]">
          <ArticleHero />
          <ArticleBody />
          <ArticleFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
